import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import { ageRangeList, emiratesList, teachingInfoList } from '../../slice/allListsSlice'
import { uploadFileToS3 } from '../../common/uploadFileToS3'
import { addTutors } from '../../slice/tutorSlice'
import Select from 'react-select'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

// Validation Schema
const tutorSchema = yup.object().shape({
    first_name: yup
        .string()
        .required('First name is required')
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name cannot exceed 50 characters')
        .matches(/^[a-zA-Z\s]+$/, 'First name can only contain letters'),

    last_name: yup
        .string()
        .required('Last name is required')
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name cannot exceed 50 characters')
        .matches(/^[a-zA-Z\s]+$/, 'Last name can only contain letters'),

    username: yup
        .string()
        .required('Username is required')
        .min(3, 'Username must be at least 3 characters')
        .max(30, 'Username cannot exceed 30 characters')
        .matches(/^[a-zA-Z0-9._-]+$/, 'Username can only contain letters, numbers, dots, hyphens, and underscores'),

    email: yup
        .string()
        .required('Email is required')
        .email('Please enter a valid email address'),

    password: yup
        .string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
            'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        ),

    confirm_password: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('password')], 'Passwords must match'),

    country_code: yup
        .string()
        .required('Country code is required'),

    phone_number: yup
        .string()
        .required('Phone number is required')
        .matches(/^[0-9]{9,15}$/, 'Phone number must be between 9 and 15 digits'),

    dob: yup
        .date()
        .transform((value, originalValue) => {
            // if the input was an empty string, treat it as null
            return originalValue === '' ? null : value
        })
        .nullable()
        .optional()
        .max(new Date(), 'Date of birth cannot be in the future')
        .test('age', 'Tutor must be at least 18 years old', function (value) {
            // skip validation if field is empty/optional
            if (!value) return true

            const cutoff = new Date()
            cutoff.setFullYear(cutoff.getFullYear() - 18)
            return value <= cutoff
        }),

    gender: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .optional()
        .oneOf(['male', 'female', 'other', null], 'Please select a valid gender'),

    lang: yup
        .string()
        .required('Language is required')
        .oneOf(['en', 'ar'], 'Please select a valid language'),

    languages_spoken: yup
        .array()
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .optional(),

    bio: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .optional()
        .min(50, 'Bio must be at least 50 characters')
        .max(500, 'Bio cannot exceed 500 characters'),

    curriculum_id: yup
        .string()
        .nullable()
        .optional(),

    subject_id: yup
        .array()
        .nullable()
        .optional(),

    age_range: yup
        .array()
        .nullable()
        .optional(),

    grades: yup
        .array()
        .nullable()
        .optional(),

    educational_qualification: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .optional(),

    subjects_taught: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .optional(),

    area_covered: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .optional(),

    emirate_state_id: yup
        .array()
        .min(1, 'Please select at least one emirate')
        .required('Emirate is required'),

    lesson_format_id: yup
        .string()
        .nullable()
        .optional(),

    emirates_id: yup
        .string()
        .required('Emirates ID number is required')
        .matches(/^784-\d{4}-\d{7}-\d{1}$/, 'Invalid Emirates ID format (e.g., 784-1990-1234567-1)'),

    profile_picture: yup
        .mixed()
        .required('Profile picture is required')
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return false
            return value[0].size <= 5242880
        })
        .test('fileType', 'Only image files are allowed', (value) => {
            if (!value || !value[0]) return false
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),

    emirates_id_front: yup
        .mixed()
        .required('Emirates ID front image is required')
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return false
            return value[0].size <= 5242880
        })
        .test('fileType', 'Only image files are allowed', (value) => {
            if (!value || !value[0]) return false
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),

    emirates_id_back: yup
        .mixed()
        .required('Emirates ID back image is required')
        .test('fileSize', 'File size must be less than 5MB', (value) => {
            if (!value || !value[0]) return false
            return value[0].size <= 5242880
        })
        .test('fileType', 'Only image files are allowed', (value) => {
            if (!value || !value[0]) return false
            return ['image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),

    tutor_permit_front: yup
        .mixed()
        .test('fileSize', 'File size must be less than 10MB', (value) => {
            if (!value || !value[0]) return true
            return value[0].size <= 10485760
        })
        .test('fileType', 'Only PDF and image files are allowed', (value) => {
            if (!value || !value[0]) return true
            return ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),

    tutor_permit_back: yup
        .mixed()
        .test('fileSize', 'File size must be less than 10MB', (value) => {
            if (!value || !value[0]) return true
            return value[0].size <= 10485760
        })
        .test('fileType', 'Only PDF and image files are allowed', (value) => {
            if (!value || !value[0]) return true
            return ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),

    education_certificate: yup
        .mixed()
        .nullable()
        .test('fileSize', 'File size must be less than 10MB', (value) => {
            if (!value || !value[0]) return true
            return value[0].size <= 10485760
        })
        .test('fileType', 'Only PDF and image files are allowed', (value) => {
            if (!value || !value[0]) return true
            return ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(value[0].type)
        }),

    video_introduction: yup
        .mixed()
        .test('fileSize', 'Video size must be less than 50MB', (value) => {
            if (!value || !value[0]) return true
            return value[0].size <= 52428800
        })
        .test('fileType', 'Only video files are allowed', (value) => {
            if (!value || !value[0]) return true
            return ['video/mp4', 'video/mpeg', 'video/quicktime'].includes(value[0].type)
        }),
})

const AddTutor = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Phone state
    const [countryCode, setCountryCode] = useState('971') // Default UAE

    // File Previews
    const [profilePreview, setProfilePreview] = useState(null)
    const [frontPreview, setFrontPreview] = useState(null)
    const [backPreview, setBackPreview] = useState(null)
    const [permitFrontPreview, setPermitFrontPreview] = useState(null)
    const [permitBackPreview, setPermitBackPreview] = useState(null)
    const [educationCertificatePreview, setEducationCertificatePreview] = useState(null)
    const [videoPreview, setVideoPreview] = useState(null)

    // File info states
    const [permitFrontInfo, setPermitFrontInfo] = useState(null)
    const [permitBackInfo, setPermitBackInfo] = useState(null)
    const [educationCertificateInfo, setEducationCertificateInfo] = useState(null)
    const [videoInfo, setVideoInfo] = useState(null)

    // Get lists from Redux
    const { ageRangeLists = [], emiratesLists = [] } = useSelector(state => state.allLists || {})
    const [curriculumLists, setCurriculumLists] = useState([])
    const [subjectLists, setSubjectLists] = useState([])
    const [gradeLists, setGradeLists] = useState([])
    const [lessonFormatLists, setLessonFormatLists] = useState([])

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(tutorSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            username: '',
            email: '',
            password: '',
            confirm_password: '',
            country_code: '+971',
            phone_number: '',
            dob: '',
            gender: '',
            lang: 'en',
            languages_spoken: [],
            bio: '',
            curriculum_id: '',
            subject_id: [],
            age_range: [],
            grades: [],
            educational_qualification: '',
            subjects_taught: '',
            emirate_state_id: [],
            emirates_id: '',
            lesson_format_id: '',
            area_covered: '',
            education_certificate: ''
        }
    })

    // Watch file inputs
    const profileFile = watch('profile_picture')
    const emiratesFrontFile = watch('emirates_id_front')
    const emiratesBackFile = watch('emirates_id_back')
    const permitFrontFile = watch('tutor_permit_front')
    const permitBackFile = watch('tutor_permit_back')
    const videoFile = watch('video_introduction')
    const educationCertificateFile = watch('education_certificate')

    // Fetch initial data
    useEffect(() => {
        dispatch(ageRangeList({}))
        dispatch(emiratesList({}))
        fetchTeachingInfo()
    }, [dispatch])

    const fetchTeachingInfo = async () => {
        // Fetch Curriculum
        const curriculumRes = await dispatch(teachingInfoList({ type: 'Curriculum' }))
        if (curriculumRes?.payload?.data) {
            setCurriculumLists(curriculumRes.payload.data)
        }

        // Fetch Subjects
        const subjectRes = await dispatch(teachingInfoList({ type: 'Subject' }))
        if (subjectRes?.payload?.data) {
            setSubjectLists(subjectRes.payload.data)
        }

        // Fetch Grades
        const gradeRes = await dispatch(teachingInfoList({ type: 'Grades / Levels Covered' }))
        if (gradeRes?.payload?.data) {
            setGradeLists(gradeRes.payload.data)
        }

        // Fetch Lesson Format
        const lessonFormatRes = await dispatch(teachingInfoList({ type: 'Lesson Format' }))
        if (lessonFormatRes?.payload?.data) {
            setLessonFormatLists(lessonFormatRes.payload.data)
        }
    }

    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    // Auto-format Emirates ID
    const formatEmiratesId = (value) => {
        // Remove all non-digit characters
        let digits = value.replace(/\D/g, '')

        // Always start with 784 for UAE
        if (digits.length > 0 && !digits.startsWith('784')) {
            // If user starts typing, prepend 784
            if (digits.length <= 3) {
                digits = '784'
            } else {
                // Keep only the part after first 3 digits
                digits = '784' + digits.slice(3)
            }
        }

        // Ensure it starts with 784
        if (digits.length === 0) {
            return ''
        }

        // Format: 784-YYYY-XXXXXXX-X
        let formatted = ''
        if (digits.length > 0) {
            formatted = digits.substring(0, 3) // 784
            if (digits.length > 3) {
                formatted += '-' + digits.substring(3, 7) // YYYY
            }
            if (digits.length > 7) {
                formatted += '-' + digits.substring(7, 14) // XXXXXXX
            }
            if (digits.length > 14) {
                formatted += '-' + digits.substring(14, 15) // X
            }
        }
        return formatted
    }

    const handleEmiratesIdChange = (e) => {
        const formatted = formatEmiratesId(e.target.value)
        setValue('emirates_id', formatted)
    }

    // Profile Picture Preview
    useEffect(() => {
        if (profileFile && profileFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => setProfilePreview(reader.result)
            reader.readAsDataURL(profileFile[0])
        } else {
            setProfilePreview(null)
        }
    }, [profileFile])

    // Emirates ID Front Preview
    useEffect(() => {
        if (emiratesFrontFile && emiratesFrontFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => setFrontPreview(reader.result)
            reader.readAsDataURL(emiratesFrontFile[0])
        } else {
            setFrontPreview(null)
        }
    }, [emiratesFrontFile])

    // Emirates ID Back Preview
    useEffect(() => {
        if (emiratesBackFile && emiratesBackFile[0]) {
            const reader = new FileReader()
            reader.onloadend = () => setBackPreview(reader.result)
            reader.readAsDataURL(emiratesBackFile[0])
        } else {
            setBackPreview(null)
        }
    }, [emiratesBackFile])

    // Permit Front Preview
    useEffect(() => {
        if (permitFrontFile && permitFrontFile[0]) {
            const file = permitFrontFile[0]
            setPermitFrontInfo({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type
            })

            if (file.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onloadend = () => setPermitFrontPreview(reader.result)
                reader.readAsDataURL(file)
            } else if (file.type === 'application/pdf') {
                setPermitFrontPreview('PDF')
            }
        } else {
            setPermitFrontPreview(null)
            setPermitFrontInfo(null)
        }
    }, [permitFrontFile])

    // Permit Back Preview
    useEffect(() => {
        if (permitBackFile && permitBackFile[0]) {
            const file = permitBackFile[0]
            setPermitBackInfo({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type
            })

            if (file.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onloadend = () => setPermitBackPreview(reader.result)
                reader.readAsDataURL(file)
            } else if (file.type === 'application/pdf') {
                setPermitBackPreview('PDF')
            }
        } else {
            setPermitBackPreview(null)
            setPermitBackInfo(null)
        }
    }, [permitBackFile])

    // Educational Certificate Preview
    useEffect(() => {
        if (educationCertificateFile && educationCertificateFile[0]) {
            const file = educationCertificateFile[0]
            setEducationCertificateInfo({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type
            })

            if (file.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onloadend = () => setEducationCertificatePreview(reader.result)
                reader.readAsDataURL(file)
            } else if (file.type === 'application/pdf') {
                setEducationCertificatePreview('PDF')
            }
        } else {
            setEducationCertificatePreview(null)
            setEducationCertificateInfo(null)
        }
    }, [educationCertificateFile])

    // Video Preview
    useEffect(() => {
        if (videoFile && videoFile[0]) {
            const file = videoFile[0]
            setVideoInfo({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type
            })

            const url = URL.createObjectURL(file)
            setVideoPreview(url)

            return () => URL.revokeObjectURL(url)
        } else {
            setVideoPreview(null)
            setVideoInfo(null)
        }
    }, [videoFile])

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            // Upload files
            const profileUpload = await uploadFileToS3(data.profile_picture[0], 'tutor/profiles')
            const frontUpload = await uploadFileToS3(data.emirates_id_front[0], 'tutor/emirates')
            const backUpload = await uploadFileToS3(data.emirates_id_back[0], 'tutor/emirates')

            let permitFrontFileName = null
            let permitBackFileName = null
            let videoFileName = null
            let educationCertificateFileName = null

            if (data.tutor_permit_front && data.tutor_permit_front[0]) {
                const permitFrontUpload = await uploadFileToS3(data.tutor_permit_front[0], 'tutor/permits')
                permitFrontFileName = permitFrontUpload.fileName
            }

            if (data.tutor_permit_back && data.tutor_permit_back[0]) {
                const permitBackUpload = await uploadFileToS3(data.tutor_permit_back[0], 'tutor/permits')
                permitBackFileName = permitBackUpload.fileName
            }

            if (data.video_introduction && data.video_introduction[0]) {
                const videoUpload = await uploadFileToS3(data.video_introduction[0], 'tutor/videos')
                videoFileName = videoUpload.fileName
            }

            if (data.education_certificate && data.education_certificate[0]) {
                const educationCertificateUpload = await uploadFileToS3(data.education_certificate[0], 'tutor/permits')
                educationCertificateFileName = educationCertificateUpload.fileName
            }

            // Build payload
            const payload = {
                first_name: data.first_name,
                last_name: data.last_name,
                username: data.username,
                email: data.email,
                password: data.password,
                country_code: data.country_code,
                phone_number: data.phone_number,
                dob: data.dob,
                gender: data.gender,
                profile_picture: profileUpload.fileName,
                languages_spoken: data.languages_spoken,
                bio: data.bio,
                curriculum_id: parseInt(data.curriculum_id),
                subject_id: data.subject_id.map(id => parseInt(id)),
                age_range: data.age_range.map(id => parseInt(id)),
                grades: data.grades.map(id => parseInt(id)),
                educational_qualification: data.educational_qualification,
                subjects_taught: data.subjects_taught,
                emirate_state_id: data.emirate_state_id.join(","),
                profile_status: 'pending',
                emirates_id: data.emirates_id,
                lesson_format_id: parseInt(data.lesson_format_id),
                education_certificate: educationCertificateFileName,
                emirates_id_front: frontUpload.fileName,
                emirates_id_back: backUpload.fileName,
                tutor_permit_front: permitFrontFileName,
                tutor_permit_back: permitBackFileName,
                video_introduction: videoFileName,
                lang: data.lang,
                area_covered: data.area_covered,
            }

            const response = await dispatch(addTutors(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response?.payload?.message || 'Tutor created successfully',
                    timer: 2000,
                    showConfirmButton: false,
                })

                // Clear all preview states
                setProfilePreview(null)
                setFrontPreview(null)
                setBackPreview(null)
                setPermitFrontPreview(null)
                setPermitBackPreview(null)
                setVideoPreview(null)
                setPermitFrontInfo(null)
                setPermitBackInfo(null)
                setVideoInfo(null)

                reset()
                setTimeout(() => navigate('/tutors'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to create tutor',
                })
            }

        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add Tutor</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/tutors')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    {/* Personal Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Personal Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* First Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                                            placeholder="Enter first name"
                                            {...register('first_name')}
                                        />
                                        {errors.first_name && (
                                            <div className="invalid-feedback">{errors.first_name.message}</div>
                                        )}
                                    </div>

                                    {/* Last Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                                            placeholder="Enter last name"
                                            {...register('last_name')}
                                        />
                                        {errors.last_name && (
                                            <div className="invalid-feedback">{errors.last_name.message}</div>
                                        )}
                                    </div>

                                    {/* Username */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Username <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                            placeholder="Enter username"
                                            {...register('username')}
                                        />
                                        {errors.username && (
                                            <div className="invalid-feedback">{errors.username.message}</div>
                                        )}
                                    </div>

                                    {/* Date of Birth */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Date of Birth <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                                            {...register('dob')}
                                        />
                                        {errors.dob && (
                                            <div className="invalid-feedback">{errors.dob.message}</div>
                                        )}
                                    </div>

                                    {/* Gender */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Gender <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
                                            {...register('gender')}
                                        >
                                            <option value="">Select gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        {errors.gender && (
                                            <div className="invalid-feedback">{errors.gender.message}</div>
                                        )}
                                    </div>

                                    {/* Language */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Preferred Language <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.lang ? 'is-invalid' : ''}`}
                                            {...register('lang')}
                                        >
                                            <option value="">Select language</option>
                                            <option value="en">English</option>
                                            <option value="ar">Arabic</option>
                                        </select>
                                        {errors.lang && (
                                            <div className="invalid-feedback">{errors.lang.message}</div>
                                        )}
                                    </div>

                                    {/* Languages Spoken */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Languages Spoken <span className="text-danger">*</span>
                                        </label>
                                        <Controller
                                            name="languages_spoken"
                                            control={control}
                                            render={({ field }) => (
                                                <div className={`border rounded p-2 ${errors.languages_spoken ? 'border-danger' : ''}`}>
                                                    {['English', 'Arabic', 'Urdu', 'Hindi', 'French', 'Spanish'].map((lang) => (
                                                        <div key={lang} className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                value={lang}
                                                                id={`lang-${lang}`}
                                                                checked={field.value.includes(lang)}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.checked
                                                                        ? [...field.value, lang]
                                                                        : field.value.filter(v => v !== lang)
                                                                    field.onChange(newValue)
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor={`lang-${lang}`}>
                                                                {lang}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                        {errors.languages_spoken && (
                                            <small className="text-danger">{errors.languages_spoken.message}</small>
                                        )}
                                    </div>

                                    {/* Emirate */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirate <span className="text-danger">*</span>
                                        </label>
                                        <Controller
                                            name="emirate_state_id"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isMulti
                                                    options={emiratesLists?.map(emirate => ({
                                                        value: emirate.id,
                                                        label: emirate.en_name,
                                                    }))}
                                                    value={emiratesLists
                                                        ?.filter(emirate => field.value.includes(emirate.id))
                                                        .map(emirate => ({
                                                            value: emirate.id,
                                                            label: emirate.en_name,
                                                        }))}
                                                    onChange={(selectedOptions) =>
                                                        field.onChange(
                                                            selectedOptions
                                                                ? selectedOptions.map(option => option.value)
                                                                : []
                                                        )
                                                    }
                                                    placeholder="Select emirate"
                                                    isClearable
                                                    styles={{
                                                        control: (base, state) => ({
                                                            ...base,
                                                            borderColor: errors.emirate_state_id ? '#dc3545' : state.isFocused ? '#86b7fe' : '#dee2e6',
                                                            boxShadow: state.isFocused
                                                                ? errors.emirate_state_id
                                                                    ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                                                                    : '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                                                                : 'none',
                                                            '&:hover': {
                                                                borderColor: errors.emirate_state_id ? '#dc3545' : '#86b7fe',
                                                            },
                                                            minHeight: '38px',
                                                            fontSize: '14px'
                                                        }),
                                                        menu: (base) => ({
                                                            ...base,
                                                            fontSize: '14px',
                                                            zIndex: 9999
                                                        }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            backgroundColor: state.isSelected
                                                                ? '#0d6efd'
                                                                : state.isFocused
                                                                    ? '#e7f1ff'
                                                                    : 'white',
                                                            color: state.isSelected ? 'white' : '#212529',
                                                            '&:active': {
                                                                backgroundColor: '#0d6efd'
                                                            }
                                                        }),
                                                    }}
                                                />
                                            )}
                                        />
                                        {errors.emirate_state_id && (
                                            <div className="invalid-feedback d-block">{errors.emirate_state_id.message}</div>
                                        )}
                                    </div>

                                    {/* Bio */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            Bio / About <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className={`form-control ${errors.bio ? 'is-invalid' : ''}`}
                                            rows="4"
                                            placeholder="Write a brief bio about yourself, your teaching experience, and expertise..."
                                            {...register('bio')}
                                        ></textarea>
                                        {errors.bio && (
                                            <div className="invalid-feedback">{errors.bio.message}</div>
                                        )}
                                        <small className="text-muted">Minimum 50 characters, maximum 500 characters</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Contact Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Email */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            placeholder="Enter email address"
                                            {...register('email')}
                                        />
                                        {errors.email && (
                                            <div className="invalid-feedback">{errors.email.message}</div>
                                        )}
                                    </div>

                                    {/* Phone Number with Country Code */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Phone Number <span className="text-danger">*</span>
                                        </label>
                                        <div className="d-flex gap-2">
                                            {/* Country Code Selector */}
                                            <div style={{ width: '150px' }}>
                                                <Controller
                                                    name="country_code"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <PhoneInput
                                                            country={'ae'} // Default to UAE
                                                            value={countryCode}
                                                            onChange={(phone, data) => {
                                                                setCountryCode(data.dialCode)
                                                                field.onChange(`+${data.dialCode}`)
                                                            }}
                                                            inputProps={{
                                                                name: 'country_code',
                                                                required: true,
                                                                disabled: true, // Disable typing
                                                            }}
                                                            containerStyle={{ width: '100%', height: '38px' }}
                                                            inputStyle={{
                                                                width: '100%',
                                                                height: '38px',
                                                                fontSize: '14px',
                                                                borderColor: errors.country_code ? '#dc3545' : '#dee2e6',
                                                                cursor: 'pointer' // Show pointer cursor
                                                            }}
                                                            buttonStyle={{
                                                                borderColor: errors.country_code ? '#dc3545' : '#dee2e6'
                                                            }}
                                                            enableSearch={true}
                                                            disableSearchIcon={false}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Phone Number Input */}
                                            <div className="flex-grow-1">
                                                <input
                                                    type="tel"
                                                    className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`}
                                                    placeholder="Enter phone number"
                                                    {...register('phone_number')}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value.replace(/\D/g, '')
                                                        if (inputValue.length <= 15) {
                                                            setValue('phone_number', inputValue)
                                                        }
                                                    }}
                                                    maxLength={15}
                                                    style={{ height: '38px' }}
                                                />
                                                {errors.phone_number && (
                                                    <div className="invalid-feedback">{errors.phone_number.message}</div>
                                                )}
                                            </div>
                                        </div>
                                        {errors.country_code && (
                                            <small className="text-danger d-block mt-1">{errors.country_code.message}</small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Security */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Account Security
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Password */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Password <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                            placeholder="Enter password"
                                            {...register('password')}
                                        />
                                        {errors.password && (
                                            <div className="invalid-feedback">{errors.password.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Password must contain uppercase, lowercase, number, and special character
                                        </small>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Confirm Password <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                                            placeholder="Confirm password"
                                            {...register('confirm_password')}
                                        />
                                        {errors.confirm_password && (
                                            <div className="invalid-feedback">{errors.confirm_password.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Teaching Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Teaching Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Curriculum */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Curriculum <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.curriculum_id ? 'is-invalid' : ''}`}
                                            {...register('curriculum_id')}
                                        >
                                            <option value="">Select curriculum</option>
                                            {curriculumLists.map((curriculum) => (
                                                <option key={curriculum.id} value={curriculum.id}>
                                                    {curriculum.option_en}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.curriculum_id && (
                                            <div className="invalid-feedback">{errors.curriculum_id.message}</div>
                                        )}
                                    </div>

                                    {/* Lesson Format */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Lession Format <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.lesson_format_id ? 'is-invalid' : ''}`}
                                            {...register('lesson_format_id')}
                                        >
                                            <option value="">Select lession format</option>
                                            {lessonFormatLists.map((lessonFormat) => (
                                                <option key={lessonFormat.id} value={lessonFormat.id}>
                                                    {lessonFormat.option_en}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.lesson_format_id && (
                                            <div className="invalid-feedback">{errors.lesson_format_id.message}</div>
                                        )}
                                    </div>

                                    {/* Subjects */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Subjects <span className="text-danger">*</span>
                                        </label>
                                        <Controller
                                            name="subject_id"
                                            control={control}
                                            render={({ field }) => (
                                                <div
                                                    className={`border rounded p-2 ${errors.subject_id ? 'border-danger' : ''}`}
                                                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                                                >
                                                    {subjectLists.map((subject) => (
                                                        <div key={subject.id} className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                value={subject.id}
                                                                id={`subject-${subject.id}`}
                                                                checked={field.value.includes(subject.id)}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.checked
                                                                        ? [...field.value, subject.id]
                                                                        : field.value.filter(v => v !== subject.id)
                                                                    field.onChange(newValue)
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor={`subject-${subject.id}`}>
                                                                {subject.option_en}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                        {errors.subject_id && (
                                            <small className="text-danger">{errors.subject_id.message}</small>
                                        )}
                                    </div>

                                    {/* Age Range */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Age Range <span className="text-danger">*</span>
                                        </label>
                                        <Controller
                                            name="age_range"
                                            control={control}
                                            render={({ field }) => (
                                                <div
                                                    className={`border rounded p-2 ${errors.age_range ? 'border-danger' : ''}`}
                                                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                                                >
                                                    {ageRangeLists.map((age) => (
                                                        <div key={age.id} className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                value={age.id}
                                                                id={`age-${age.id}`}
                                                                checked={field.value.includes(age.id)}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.checked
                                                                        ? [...field.value, age.id]
                                                                        : field.value.filter(v => v !== age.id)
                                                                    field.onChange(newValue)
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor={`age-${age.id}`}>
                                                                {age.name}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                        {errors.age_range && (
                                            <small className="text-danger">{errors.age_range.message}</small>
                                        )}
                                    </div>

                                    {/* Grades */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Grades / Levels <span className="text-danger">*</span>
                                        </label>
                                        <Controller
                                            name="grades"
                                            control={control}
                                            render={({ field }) => (
                                                <div
                                                    className={`border rounded p-2 ${errors.grades ? 'border-danger' : ''}`}
                                                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                                                >
                                                    {gradeLists.map((grade) => (
                                                        <div key={grade.id} className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                value={grade.id}
                                                                id={`grade-${grade.id}`}
                                                                checked={field.value.includes(grade.id)}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.checked
                                                                        ? [...field.value, grade.id]
                                                                        : field.value.filter(v => v !== grade.id)
                                                                    field.onChange(newValue)
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor={`grade-${grade.id}`}>
                                                                {grade.option_en}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                        {errors.grades && (
                                            <small className="text-danger">{errors.grades.message}</small>
                                        )}
                                    </div>

                                    {/* Educational Qualification */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Educational Qualification <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.educational_qualification ? 'is-invalid' : ''}`}
                                            placeholder="e.g., Master of Science in Mathematics Education – Khalifa University"
                                            {...register('educational_qualification')}
                                        />
                                        {errors.educational_qualification && (
                                            <div className="invalid-feedback">{errors.educational_qualification.message}</div>
                                        )}
                                    </div>

                                    {/* Subjects Taught */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Subjects Taught (Detailed) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.subjects_taught ? 'is-invalid' : ''}`}
                                            placeholder="e.g., Algebra, Geometry, Calculus, Statistics"
                                            {...register('subjects_taught')}
                                        />
                                        {errors.subjects_taught && (
                                            <div className="invalid-feedback">{errors.subjects_taught.message}</div>
                                        )}
                                    </div>

                                    {/* Areas Covered */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Area Covered (Detailed) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.area_covered ? 'is-invalid' : ''}`}
                                            placeholder="e.g., Dubai, Abu Dhabi, Sharjah"
                                            {...register('area_covered')}
                                        />
                                        {errors.area_covered && (
                                            <div className="invalid-feedback">{errors.area_covered.message}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Documents & Media */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Documents & Media
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Emirates ID Number with Auto-Format */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Emirates ID Number <span className="text-danger">*</span>
                                        </label>
                                        <Controller
                                            name="emirates_id"
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.emirates_id ? 'is-invalid' : ''}`}
                                                    placeholder="784-YYYY-XXXXXXX-X"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const formatted = formatEmiratesId(e.target.value)
                                                        field.onChange(formatted)
                                                    }}
                                                    onFocus={(e) => {
                                                        // Pre-fill with 784- if field is empty
                                                        if (!e.target.value) {
                                                            field.onChange('784')
                                                        }
                                                    }}
                                                    maxLength={18}
                                                />
                                            )}
                                        />
                                        {errors.emirates_id && (
                                            <div className="invalid-feedback d-block">{errors.emirates_id.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Format: 784-YYYY-XXXXXXX-X (must start with 784, auto-formatted)
                                            <br />
                                            Example: 784-1990-1234567-1
                                        </small>
                                    </div>

                                    {/* Profile Picture */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Profile Picture <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.profile_picture ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('profile_picture')}
                                        />
                                        {errors.profile_picture && (
                                            <div className="invalid-feedback">{errors.profile_picture.message}</div>
                                        )}
                                        {profilePreview && (
                                            <div className="mt-3 text-center">
                                                <div className="d-inline-block position-relative">
                                                    <img
                                                        src={profilePreview}
                                                        alt="Profile Preview"
                                                        style={{
                                                            width: '150px',
                                                            height: '150px',
                                                            objectFit: 'cover',
                                                            borderRadius: '50%',
                                                            border: '3px solid #0d6efd',
                                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                        }}
                                                    />
                                                    <div className="mt-2">
                                                        <small className="text-muted">
                                                            {profileFile?.[0]?.name} ({profileFile?.[0]?.size ? formatFileSize(profileFile[0].size) : ''})
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Emirates ID Front */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirates ID Front <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.emirates_id_front ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('emirates_id_front')}
                                        />
                                        {errors.emirates_id_front && (
                                            <div className="invalid-feedback">{errors.emirates_id_front.message}</div>
                                        )}
                                        {frontPreview && (
                                            <div className="mt-3">
                                                <div className="card">
                                                    <img
                                                        src={frontPreview}
                                                        alt="Emirates ID Front"
                                                        className="card-img-top"
                                                        style={{
                                                            maxHeight: '200px',
                                                            objectFit: 'contain',
                                                            padding: '10px'
                                                        }}
                                                    />
                                                    <div className="card-body p-2">
                                                        <small className="text-muted">
                                                            {emiratesFrontFile?.[0]?.name}<br />
                                                            {emiratesFrontFile?.[0]?.size ? formatFileSize(emiratesFrontFile[0].size) : ''}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Emirates ID Back */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Emirates ID Back <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.emirates_id_back ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png"
                                            {...register('emirates_id_back')}
                                        />
                                        {errors.emirates_id_back && (
                                            <div className="invalid-feedback">{errors.emirates_id_back.message}</div>
                                        )}
                                        {backPreview && (
                                            <div className="mt-3">
                                                <div className="card">
                                                    <img
                                                        src={backPreview}
                                                        alt="Emirates ID Back"
                                                        className="card-img-top"
                                                        style={{
                                                            maxHeight: '200px',
                                                            objectFit: 'contain',
                                                            padding: '10px'
                                                        }}
                                                    />
                                                    <div className="card-body p-2">
                                                        <small className="text-muted">
                                                            {emiratesBackFile?.[0]?.name}<br />
                                                            {emiratesBackFile?.[0]?.size ? formatFileSize(emiratesBackFile[0].size) : ''}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Tutor Permit Front */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Tutor Permit Front (Optional)
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.tutor_permit_front ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                                            {...register('tutor_permit_front')}
                                        />
                                        {errors.tutor_permit_front && (
                                            <div className="invalid-feedback">{errors.tutor_permit_front.message}</div>
                                        )}
                                        {permitFrontPreview && (
                                            <div className="mt-3">
                                                {permitFrontPreview === 'PDF' ? (
                                                    <div className="alert alert-info d-flex align-items-center">
                                                        <i className="mdi mdi-file-pdf-box fs-1 me-3 text-danger"></i>
                                                        <div>
                                                            <strong>{permitFrontInfo?.name}</strong><br />
                                                            <small>Size: {permitFrontInfo?.size}</small>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="card">
                                                        <img
                                                            src={permitFrontPreview}
                                                            alt="Permit Front"
                                                            className="card-img-top"
                                                            style={{
                                                                maxHeight: '200px',
                                                                objectFit: 'contain',
                                                                padding: '10px'
                                                            }}
                                                        />
                                                        <div className="card-body p-2">
                                                            <small className="text-muted">
                                                                {permitFrontInfo?.name}<br />
                                                                {permitFrontInfo?.size}
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Tutor Permit Back */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Tutor Permit Back (Optional)
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.tutor_permit_back ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                                            {...register('tutor_permit_back')}
                                        />
                                        {errors.tutor_permit_back && (
                                            <div className="invalid-feedback">{errors.tutor_permit_back.message}</div>
                                        )}
                                        {permitBackPreview && (
                                            <div className="mt-3">
                                                {permitBackPreview === 'PDF' ? (
                                                    <div className="alert alert-info d-flex align-items-center">
                                                        <i className="mdi mdi-file-pdf-box fs-1 me-3 text-danger"></i>
                                                        <div>
                                                            <strong>{permitBackInfo?.name}</strong><br />
                                                            <small>Size: {permitBackInfo?.size}</small>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="card">
                                                        <img
                                                            src={permitBackPreview}
                                                            alt="Permit Back"
                                                            className="card-img-top"
                                                            style={{
                                                                maxHeight: '200px',
                                                                objectFit: 'contain',
                                                                padding: '10px'
                                                            }}
                                                        />
                                                        <div className="card-body p-2">
                                                            <small className="text-muted">
                                                                {permitBackInfo?.name}<br />
                                                                {permitBackInfo?.size}
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Educational Certificate */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Educational Certificate (Optional)
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.education_certificate ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                                            {...register('education_certificate')}
                                        />
                                        {errors.education_certificate && (
                                            <div className="invalid-feedback">{errors.education_certificate.message}</div>
                                        )}
                                        {educationCertificatePreview && (
                                            <div className="mt-3">
                                                {educationCertificatePreview === 'PDF' ? (
                                                    <div className="alert alert-info d-flex align-items-center">
                                                        <i className="mdi mdi-file-pdf-box fs-1 me-3 text-danger"></i>
                                                        <div>
                                                            <strong>{educationCertificateInfo?.name}</strong><br />
                                                            <small>Size: {educationCertificateInfo?.size}</small>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="card">
                                                        <img
                                                            src={educationCertificatePreview}
                                                            alt="Permit Back"
                                                            className="card-img-top"
                                                            style={{
                                                                maxHeight: '200px',
                                                                objectFit: 'contain',
                                                                padding: '10px'
                                                            }}
                                                        />
                                                        <div className="card-body p-2">
                                                            <small className="text-muted">
                                                                {educationCertificateInfo?.name}<br />
                                                                {educationCertificateInfo?.size}
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Video Introduction */}
                                    <div className="col-md-12">
                                        <label className="form-label">
                                            Video Introduction (Optional)
                                        </label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.video_introduction ? 'is-invalid' : ''}`}
                                            accept="video/mp4,video/mpeg,video/quicktime"
                                            {...register('video_introduction')}
                                        />
                                        {errors.video_introduction && (
                                            <div className="invalid-feedback">{errors.video_introduction.message}</div>
                                        )}
                                        <small className="text-muted">Max size: 50MB. Formats: MP4, MPEG, MOV</small>

                                        {videoPreview && (
                                            <div className="mt-3">
                                                <div className="card">
                                                    <video
                                                        controls
                                                        className="card-img-top"
                                                        style={{ maxHeight: '300px', backgroundColor: '#000' }}
                                                    >
                                                        <source src={videoPreview} type={videoInfo?.type} />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                    <div className="card-body p-2">
                                                        <small className="text-muted">
                                                            <i className="mdi mdi-video me-1"></i>
                                                            {videoInfo?.name}<br />
                                                            Size: {videoInfo?.size}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/tutors')}
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px',
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Create Tutor'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddTutor;